<script lang="ts">
  import { CATEGORY_META, categoryLabel, categoryShort, type Card } from '@banger/shared';

  let { card, marked, eligible = [], pending = null, fresh = null, onpick, cutoff = 2000, outline = [], wild = false }: {
    /** Before/After cutoff, for the square labels. */
    cutoff?: number | null;
    card: Card;
    marked: boolean[];
    /** Mark mode: these cells pulse and are tappable; the rest dim. */
    eligible?: number[];
    /** Optimistically marked cell (tapped, not yet confirmed by the server). */
    pending?: number | null;
    /** Cell marked this round, shown with a pop. */
    fresh?: number | null;
    onpick?: (cell: number) => void;
    /** Album Track 2: the Remix pattern's cells get a dashed white outline. */
    outline?: readonly number[];
    /** Album Track 3: eligible squares glow platinum (every empty square is wild). */
    wild?: boolean;
  } = $props();

  const markMode = $derived(eligible.length > 0 && pending === null);
  const inPattern = $derived(new Set(outline));
</script>

<div class="grid" class:mark-mode={markMode} role="grid" aria-label="Your bingo card">
  {#each card.cells as cell, i (i)}
    {@const free = cell === 'free'}
    {@const meta = free ? null : CATEGORY_META[cell]}
    {@const short = free ? '' : categoryShort(cell, cutoff)}
    {@const on = free || marked[i] === true || pending === i}
    {@const canTap = markMode && eligible.includes(i)}
    {@const label = free ? 'Free square' : `${categoryLabel(cell, cutoff)}${on ? ', marked' : ''}`}
    {#if canTap}
      <button
        type="button"
        class="sq eligible"
        class:wild
        class:goal={inPattern.has(i)}
        style="--c:{wild ? '#e9eef6' : meta!.color}"
        aria-label="Mark {label}"
        onclick={() => onpick?.(i)}
      >{short}</button>
    {:else}
      <div
        class="sq"
        class:free
        class:on
        class:fresh={fresh === i || pending === i}
        class:dim={markMode}
        class:goal={inPattern.has(i)}
        style="--c:{meta?.color ?? '#fff'}"
        role="gridcell"
        aria-label={label}
      >{free ? '★' : on ? '✓' : short}</div>
    {/if}
  {/each}
</div>

<style>
  .grid {
    display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px;
    width: 100%; max-width: 420px; margin: 0 auto;
  }
  .sq {
    aspect-ratio: 1; min-width: 0; min-height: 0; border-radius: 10px; border: 2.5px solid var(--c); color: var(--c);
    background: rgba(10, 6, 24, 0.55); font: inherit; font-weight: 900; font-size: clamp(0.85rem, 4vw, 1.15rem);
    display: flex; align-items: center; justify-content: center; padding: 0;
    transition: opacity 0.2s, transform 0.15s;
  }
  .sq.on { background: var(--c); color: #0a0618; box-shadow: 0 0 12px var(--c), 0 0 26px color-mix(in srgb, var(--c) 45%, transparent); font-size: clamp(1.2rem, 6vw, 1.6rem); }
  .sq.free { background: #fff; border-color: #fff; color: #0a0618; box-shadow: 0 0 12px rgba(255, 255, 255, 0.6); }
  .sq.dim { opacity: 0.28; }
  .sq.fresh { animation: pop 0.45s cubic-bezier(0.2, 1.6, 0.4, 1); opacity: 1; }
  .eligible {
    cursor: pointer; touch-action: manipulation;
    box-shadow: 0 0 14px var(--c), inset 0 0 12px color-mix(in srgb, var(--c) 40%, transparent);
    animation: pulse 0.9s ease-in-out infinite alternate;
  }
  .eligible:active { transform: scale(0.92); }
  /* Track 2: the pattern squares to aim for. The outline sits in the 6px gap, so nothing clips it. */
  .sq.goal { outline: 3px dashed #fff; outline-offset: 2px; }
  .sq.goal.dim { opacity: 0.55; }
  /* Track 3: wild squares glow platinum white, whatever their category. */
  .eligible.wild {
    color: #fff; border-color: #fff;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.22), rgba(184, 196, 214, 0.12));
    text-shadow: 0 0 8px #fff;
  }
  @keyframes pulse {
    from { box-shadow: 0 0 6px var(--c), inset 0 0 6px color-mix(in srgb, var(--c) 30%, transparent); transform: scale(0.96); }
    to { box-shadow: 0 0 22px var(--c), inset 0 0 16px color-mix(in srgb, var(--c) 55%, transparent); transform: scale(1.04); }
  }
  @keyframes pop { 0% { transform: scale(0.6); } 70% { transform: scale(1.15); } 100% { transform: scale(1); } }
  @media (prefers-reduced-motion: reduce) {
    .eligible { transform: none; }
  }
</style>
